import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document, SchemaField } from 'src/app/modules/core/store/models/document.model';
import { DistributionList, ChannelType, DistributionListType } from 'src/app/modules/communications/store/communications';
import { FilterGroup } from 'src/app/modules/core/store/models/filters.model';
import { DynamicField } from 'src/app/modules/core/components/fields/dynamic-field';
import { View } from 'src/app/modules/management/store/workflow';
import { FieldDataType, QueryParams, Workflow } from 'src/app/modules/core/store/models';
import { cloneDeep } from 'lodash-es';
import * as fromComm from 'src/app/modules/communications/store/communications';
import * as fromRouting from 'src/app/modules/core/store/routing';
import { IServerSideDatasource, ValueFormatterParams } from 'ag-grid-enterprise';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import { getCellRenderer, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { PicklistService } from 'src/app/modules/core/services/picklist/picklist.service';
import { GUID_EMPTY } from '@app/core/constants';
import { OrgBusinessUnit } from '@app/modules/core/org-business-units/org-business-unit.model';
import { OrgBusinessUnitsApiService } from '@app/modules/core/org-business-units/org-business-units-api.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import XLSX from 'xlsx';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'topo-distribution-list-edit',
  templateUrl: './distribution-list-edit.component.html',
  styleUrls: ['./distribution-list-edit.component.scss'],
  standalone: false
})
export class DistributionListEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableServerComponent, { static: true }) dataTable: DataTableServerComponent;
  selectedTabIndex = 0;
  tabs: string[] = [];
  datasource: any;
  columDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  distributionList: DistributionList = {
    uId: null,
    name: null,
    description: null,
    channelType: ChannelType.Email,
    channelUId: null,
    nameFieldUId: null,
    destinationFieldUId: null,
    isActive: null,
    createdOn: null,
    updatedOn: null,
    lastEditedByName: null,
    lastEditedById: null,
    queryMatchRule: {
      uId: null,
      datasetUId: null,
      root: {
        uId: null,
        filters: [],
        filterGroups: [],
        groupType: null
      }
    },
    businessUnitUIds: null,
    distributionListType: DistributionListType.None
  };
  editDocumentView: View;
  dataset: Dataset;
  nameFields: SchemaField[] = [];
  destinationFields: SchemaField[] = [];
  selectedDocument: Document;
  documentControls: DynamicField[] = [];
  showEditDocument: boolean;
  documents$: Observable<Document[]>;
  tableFields: SchemaField[] = [];
  destroy$ = new Subject<void>();
  filterGroup: FilterGroup;
  readonlyMode: boolean = false;
  isLoading = false;

  organizationUId: string;
  guidEmpty = GUID_EMPTY;

  isRightDrawerOpen: boolean;

  businessUnits: OrgBusinessUnit[];
  selectedUnitUIds = [];
  hasBusinessUnits: boolean;
  rootUnitUId: string;

  private notify = inject(SystemNotificationsService);
  private spinner = inject(NgxSpinnerService);

  protected readonly RECORD_LIMIT = 40000000;

  constructor(
    private store: Store<fromComm.State>,
    private translate: TranslateService,
    private gridHelper: GridHelperService,
    private picklistService: PicklistService,
    private commApiService: CommunicationsApiService,
    private orgBusinessUnitsApiService: OrgBusinessUnitsApiService,
    protected userContextService: UserContextService
  ) {}

  ngOnInit(): void {
    this.tabs.push(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.TAB-OVERVIEW'));
    this.tabs.push(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.TAB-FILTERS'));

    this.orgBusinessUnitsApiService.hasOrgBusinessUnits().subscribe(hasBusinessUnits => {
      this.hasBusinessUnits = hasBusinessUnits;
    });

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        this.organizationUId = userContext?.organizationUId;
      });

    this.store
      .select(fromComm.selectSelectedDistributionList)
      .pipe(takeUntil(this.destroy$))
      .subscribe(distributionList => {
        this.distributionList = !!distributionList ? cloneDeep(distributionList) : null;

        if (!!this.distributionList) {
          const channelType = this.distributionList.channelType;
          this.distributionList.channelTypeDisplay = this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${channelType}`);
          this.store.dispatch(fromComm.getDatasetFromApi({ datasetUId: this.distributionList?.queryMatchRule?.datasetUId }));
          this.filterGroup = cloneDeep(this.distributionList.queryMatchRule.root);
          this.readonlyMode = this.distributionList.distributionListType != DistributionListType.None;
          this.orgBusinessUnitsApiService
            .getOrgBusinessUnitsForUser(this.userContextService.getUserContext().userProfileUId)
            .subscribe(businessUnits => {
              this.processBusinessUnits(businessUnits);
            });
        }
      });

    this.store
      .select(fromComm.selectCommunicationCenterDataset)
      .pipe(takeUntil(this.destroy$))
      .subscribe(dataset => {
        this.dataset = dataset;

        if (!!this.dataset) {
          this.loadDatasetFieldSelections();
          this.loadDatasetDocuments();
        }
      });

    this.documents$ = this.store.select(fromComm.selectDistributionListDataDocuments);
    this.datasource = this.getServerSideDatasource();
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  ngOnDestroy(): void {
    this.gridHelper.clearSearchFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigate(path: string) {
    this.store.dispatch(
      fromRouting.go({
        routeData: { path: [path] }
      })
    );
  }

  private cloneFilterGroup(): FilterGroup {
    try {
      return JSON.parse(JSON.stringify(this.filterGroup));
    } catch (e) {
      console.warn('Could not clone Filter Group');
      return null;
    }
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: params => {
        const translatedParams = this.gridHelper.gridParamsToRequestParams(params.request);
        const searchFilterGrp = this.gridHelper.getSearchFilter();

        var customFilter = this.cloneFilterGroup();
        if (!!searchFilterGrp) {
          if (!!customFilter?.filterGroups) {
            customFilter.filterGroups.push(searchFilterGrp);
          } else {
            customFilter.filterGroups = [searchFilterGrp];
          }
        }

        translatedParams.skip = this.skip;
        translatedParams.take = this.take;

        this.commApiService
          .getDocumentsWithFilter(this.distributionList?.queryMatchRule?.datasetUId, translatedParams, customFilter)
          .subscribe(
            resp => {
              const response = this.gridHelper.queryResultToGridData(resp);
              this.totalCount = response?.rowCount;

              params.success({
                rowData: response?.rowData,
                rowCount: response?.rowCount
              });
            },
            error => params.fail()
          );
      }
    };
  }

  tabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  cancelClick(): void {
    this.store.dispatch(fromComm.cancelEditDistributionList());
  }

  saveClick(): void {
    this.store.dispatch(
      fromComm.saveDistributionListToApi({
        distributionList: {
          ...this.distributionList,
          queryMatchRule: {
            ...this.distributionList.queryMatchRule,
            root: {
              ...this.filterGroup
            }
          }
        }
      })
    );
  }

  viewDocumentClick(document: Document): void {
    this.selectedDocument = document;
    this.isRightDrawerOpen = true;
  }

  closeDocumentClick(): void {
    this.selectedDocument = null;
    this.isRightDrawerOpen = false;
  }

  onConfirmResponse(response: boolean): void {}

  loadDatasetDocuments(): void {
    if (!this.dataset) {
      return;
    }

    const schema = this.dataset.schema;
    const workflow = this.dataset.workflow;
    const detailView = workflow.views.find(vw => vw.name === 'detail');
    const tableView = workflow.views.find(vw => vw.name === 'table');
    const viewComponents = tableView.controls;

    this.tableFields = viewComponents.map(vc => schema.fields.find(s => s.uId === vc.dataSetFieldUId)).filter(f => f !== null);
    this.editDocumentView = detailView?.controls?.length > 0 ? (detailView?.controls[0] as View) : null;
    this.columDefs = this.generateColumnsForTable(this.tableFields.map(t => t.systemName));
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  generateColumnsForTable = (fieldNames: string[]) => {
    return this.generateTableColumns(fieldNames, [this.dataset], [this.dataset.workflow], this.organizationUId);
  };

  loadDatasetFieldSelections(): void {
    const nameFields = this.dataset.schema.fields.filter(f => f.dataType === FieldDataType.Text);
    this.nameFields = nameFields;

    let destFields: SchemaField[];
    if (this.distributionList.channelType === ChannelType.Email) {
      destFields = this.dataset.schema.fields.filter(f => f.dataType === FieldDataType.Email);
    } else if (this.distributionList.channelType === ChannelType.Sms) {
      destFields = this.dataset.schema.fields.filter(f => f.dataType === FieldDataType.Phone);
    } else if (this.distributionList.channelType === ChannelType.MassNotifications) {
      destFields = this.dataset.schema.fields.filter(f => f.dataType === FieldDataType.Text || f.dataType === FieldDataType.Number);
    }

    this.destinationFields = destFields;
  }

  listTypeChange(): void {
    this.loadDatasetFieldSelections();
  }

  datasetChange(): void {
    this.loadDatasetFieldSelections();
  }

  filterChanged(updatedFilterGroup: FilterGroup) {
    this.filterGroup = updatedFilterGroup;
    this.dataTable.refreshGrid();
  }

  generateTableColumns(fieldNames: string[], datasets: Dataset[], workflows: Workflow[], organizationUId: string) {
    const columns = [];
    columns.push({
      colId: '_actions',
      headerName: '',
      field: '',
      floatingFilter: false,
      sortable: false,
      resizable: false,
      minWidth: 64,
      suppressHeaderMenuButton: true,
      cellRenderer: 'actionCellComponent',
      cellRendererParams: {
        location: 'right',
        actions: [
          {
            label: this.translate.instant(`MANAGEMENT.DATASETS.DETAIL-VIEW`),
            icon: 'icon-small_solid_eye',
            click: data => {
              this.viewDocumentClick(data);
            }
          }
        ]
      },
      width: 64,
      maxWidth: 64
    });

    fieldNames.map(c => {
      const parts = c.split(':');
      let fieldName = '';
      let dataset = null;
      if (parts.length > 1) {
        dataset = datasets.find(ds => ds.uId === parts[0]);
        if (!!!dataset) {
          dataset = datasets[0];
        }
        fieldName = parts[1];
      } else {
        dataset = datasets[0];
        fieldName = c;
      }
      const field = dataset.schema.fields.find(f => f.systemName === fieldName);
      if (!!field) {
        let cellRenderer: string;
        cellRenderer = getCellRenderer(field.dataType);

        const valueGetter = getValueGetter(field.dataType);

        columns.push({
          headerName: field.displayName,
          field: field.systemName,
          colId: field.systemName,
          enableRowGroup: true,
          sortable: true,
          resizable: true,
          minWidth: 215,
          menuTabs: [],
          cellRenderer,
          cellRendererParams: {
            schemaField: field,
            picklistService: this.picklistService,
            organizationUId: organizationUId,
            workflows
          },
          valueGetter
        });
      }
    });

    return columns;
  }

  private addSelectionColumns(columns: any[]): void {
    columns.push({
      colId: '_actions',
      headerName: '',
      field: '',
      floatingFilter: false,
      sortable: false,
      resizable: false,
      minWidth: 64,
      suppressHeaderMenuButton: true,
      cellRenderer: 'actionCellComponent',
      cellRendererParams: {
        location: 'left',
        actions: [
          {
            label: this.translate.instant(`MANAGEMENT.DATASETS.DETAIL-VIEW`),
            icon: 'icon-small_solid_eye',
            click: data => {
              this.viewDocumentClick(data);
            }
          }
        ]
      },
      width: 64,
      maxWidth: 64
    });
  }

  private processBusinessUnits(businessUnits: OrgBusinessUnit[]): void {
    const rootUnit = businessUnits?.find(u => !!!u.parentUId);
    this.rootUnitUId = null;
    this.selectedUnitUIds = [];
    if (!!rootUnit) {
      rootUnit.name = this.translate.instant('MANAGEMENT.MENU.GLOBAL-UNIT', { name: rootUnit.name });
      this.rootUnitUId = rootUnit.uId;
    }
    if (!!this.distributionList) {
      if (this.distributionList.businessUnitUIds?.length > 0) {
        this.selectedUnitUIds = this.distributionList.businessUnitUIds.sort((c1, c2) =>
          businessUnits.find(b => b.uId == c1)?.level > businessUnits.find(b => b.uId == c2)?.level ? 1 : -1
        );
      }
    }
    this.businessUnits = businessUnits;
  }

  onBusinessUnitsSelectionChanged(selectedUIds: string[]): void {
    this.selectedUnitUIds = selectedUIds;
    this.distributionList.businessUnitUIds = selectedUIds;
  }

  async downloadDistributionListCSV(columns: string[]): Promise<void> {
    if (this.totalCount > this.RECORD_LIMIT) {
      this.notify.error(this.translate.instant('COMMUNICATIONS.DISTRIBUTION-LISTS.EXPORT-FAIL-ERROR'));
      return;
    }

    const datasetUId = this.distributionList?.queryMatchRule?.datasetUId;

    if (!datasetUId) {
      this.notify.error(this.translate.instant('COMMUNICATIONS.DISTRIBUTION-LISTS.EXPORT-FAIL-ERROR'));
      console.error('No Dataset UId detected for Distribution List export');
      return;
    }

    this.spinner.show('dist-list');

    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([]);

      const { headerRow, fields } = columns.reduce(
        (acc, colId) => {
          const colDef = this.columDefs.find(def => def.field === colId);

          if (!!colDef) {
            const { headerName, field } = colDef;

            acc.headerRow.push(headerName);
            acc.fields.push(field);
          }
          return acc;
        },
        {
          headerRow: [],
          fields: []
        }
      );

      XLSX.utils.sheet_add_aoa(worksheet, [headerRow]);

      worksheet['!cols'] = headerRow.map(() => ({ wpx: 175 }));

      const queryParams: QueryParams = {
        skip: 0,
        take: 500
      };

      let isWorking = true;

      const filterGroup = this.cloneFilterGroup();

      while (isWorking) {
        try {
          const { data, totalCount, skip, take } = await this.commApiService
            .getDocumentsWithFilter(datasetUId, queryParams, filterGroup)
            .toPromise();

          if (data.length && totalCount) {
            const rows = data.map(document => fields.map(f => document[f]));

            XLSX.utils.sheet_add_aoa(worksheet, rows, { origin: -1 });

            queryParams.skip = skip + take;
          } else {
            isWorking = false;
            break;
          }
        } catch (e) {
          isWorking = false;
          this.notify.error(this.translate.instant('COMMUNICATIONS.DISTRIBUTION-LISTS.EXPORT-FAIL-ERROR'));
          console.error(e);
          break;
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, (this.dataset?.name ?? 'Sheet 1').slice(0, 31));
      XLSX.writeFile(workbook, `${this.distributionList?.name ?? 'Distribution List'}.xlsx`);
    } catch (e) {
      this.notify.error(this.translate.instant('COMMUNICATIONS.DISTRIBUTION-LISTS.EXPORT-FAIL-ERROR'));
      console.error(e);
    }

    this.spinner.hide('dist-list');
  }
}
